import React, { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

/**
 * Vorher/Nachher-Vergleich mit ziehbarem Slider.
 * beforeSrc = Original, afterSrc = Fotobooth-Ergebnis.
 */
export const ComparisonSlider = ({ beforeSrc, afterSrc, beforeLabel = "Vorher", afterLabel = "Nachher", className = "", testId = "comparison-slider" }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const updateFromEvent = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromEvent(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) updateFromEvent(e.clientX);
  };
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      className={`relative select-none overflow-hidden rounded-xl border border-border ${className}`}
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      data-testid={testId}
    >
      <img src={beforeSrc} alt={beforeLabel} className="block w-full aspect-[4/3] object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={afterSrc} alt={afterLabel} className="block w-full h-full aspect-[4/3] object-cover" draggable={false} />
      </div>
      {/* Trennlinie + Griff */}
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.6)]" />
        <button
          type="button"
          aria-label="Vergleich verschieben"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-black shadow-lg cursor-ew-resize focus-visible:ring-2 focus-visible:ring-sky-400"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
            if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
          }}
          data-testid={`${testId}-handle`}
        >
          <ChevronsLeftRight className="h-4 w-4" />
        </button>
      </div>
      <span className="absolute top-2 left-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">{beforeLabel}</span>
      <span className="absolute top-2 right-2 rounded-full bg-sky-500/90 px-2 py-0.5 text-[10px] font-bold text-black">{afterLabel}</span>
    </div>
  );
};

export default ComparisonSlider;
